import { useState } from "react";
import { Check } from "lucide-react";
import { ReplyText } from "../../agent/rail/RailParts";

/**
 * Creo's two questions while a month plans (Figma 08, 13 Oct, "Plan January
 * tapped: Creo plans and asks"): "While I plan January: what should it do for
 * Fatafeat?" and "Anything to highlight? A launch, a season, a date." Rendered
 * locally in the rail through agentRail.ask: no model call. The answer is
 * saved as the month's planning note (PATCH /plans/:id/guidance); it shapes the
 * posts' ideas as they land, and the layout when the planner has not been
 * called yet. Skip leaves the month to plan from the strategy alone.
 */
export default function PlanAskCard({
  monthName,
  clientName,
  onAnswer,
  onDone,
}: {
  monthName: string;
  clientName: string;
  /** Saves the note; resolves with what to say back. */
  onAnswer: (text: string) => Promise<string>;
  onDone: () => void;
}) {
  const [text, setText] = useState("");
  const [sent, setSent] = useState<{ text: string; reply: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const question = `While I plan ${monthName}: what should it do for ${clientName}?`;
  const send = async () => {
    const line = text.trim();
    if (!line || busy) return;
    setBusy(true);
    try {
      const reply = await onAnswer(line);
      setSent({ text: line, reply });
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <>
        <div className="m7r-answered">
          <div className="m7r-answered-item">
            <p className="m7r-answered-q">{question}</p>
            <div className="m7r-answered-pick">
              <Check aria-hidden strokeWidth={2} />
              <span>{sent.text}</span>
            </div>
          </div>
        </div>
        <ReplyText content={sent.reply} />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <ReplyText content={`${question}\n\nAnything to highlight? A launch, a season, a date.`} />
      <section className="m7r-card" data-live aria-label="Creo is asking">
        <div className="m7r-card-head">
          <p className="m7r-card-question">Your note for {monthName}</p>
          <p className="m7r-card-hint">It shapes the posts’ ideas as they land. Skip to plan from the strategy alone.</p>
        </div>
        <div className="px-4 pb-1">
          <textarea
            value={text}
            rows={3}
            maxLength={4000}
            placeholder="A launch, a season, a date, what to lean into or keep clear of…"
            aria-label={question}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || event.shiftKey) return;
              event.preventDefault();
              void send();
            }}
            className="w-full resize-y rounded-card bg-co-tonal px-3 py-2.5 text-[14px] leading-5 text-co-text outline-none placeholder:text-co-muted focus:shadow-[inset_0_0_0_1px_var(--co-accent)]"
          />
        </div>
        <div className="m7r-card-actions">
          <button type="button" className="m7r-text-btn" onClick={onDone} disabled={busy}>
            Skip
          </button>
          <button type="button" className="m7r-primary" disabled={!text.trim() || busy} onClick={() => void send()}>
            Send
          </button>
        </div>
      </section>
    </div>
  );
}
